import { z } from "zod";
import { channelIdSchema } from "./channels.js";
/** The most pins one channel keeps; past this its Files tab is the place. */
export const CHANNEL_PIN_LIMIT = 50;
export const channelPinIdSchema = z.string().trim().min(1).max(200);
/**
 * Where a pin leads: a web address, or a path in this workspace such as a
 * session, an artifact, a file in Knowledge or another channel. Paths keep
 * pins working when the workspace is reached at another address.
 */
export const channelPinHrefSchema = z.string().trim().min(1).max(2048).refine(isChannelPinHref, {
    message: "A pin must be a web address (http or https) or a path in this workspace.",
});
const channelPinLabelSchema = z.string().trim().min(1).max(200);
export function isChannelPinHref(href) {
    if (href.startsWith("/"))
        return !href.startsWith("//") && !href.includes("\\");
    try {
        const url = new URL(href);
        return (url.protocol === "https:" || url.protocol === "http:") && !url.username && !url.password;
    }
    catch {
        return false;
    }
}
/**
 * A link pinned to a channel's Overview, in the order its members arranged.
 * A pin grants nothing: what it leads to keeps its own access.
 */
export const channelPinSchema = z.object({
    id: channelPinIdSchema,
    channelId: channelIdSchema,
    href: channelPinHrefSchema,
    /** The name its members gave it; null shows the target's own name. */
    label: channelPinLabelSchema.nullable(),
    position: z.number().int().nonnegative(),
    createdBy: z.string().trim().min(1),
    createdAt: z.number(),
    updatedAt: z.number(),
});
export const createChannelPinInputSchema = z.object({
    href: channelPinHrefSchema,
    label: channelPinLabelSchema.optional(),
}).strict();
export const patchChannelPinInputSchema = z.object({
    /** A new name, or null to show the target's own name again. */
    label: channelPinLabelSchema.nullable().optional(),
    /** The pin's zero-based place among the channel's pins. */
    position: z.number().int().nonnegative().max(CHANNEL_PIN_LIMIT).optional(),
}).strict().refine((input) => input.label !== undefined || input.position !== undefined, {
    message: "Name a new label or position for the pin.",
});
export const listChannelPinsResponseSchema = z.object({
    pins: z.array(channelPinSchema),
});
export const channelPinResponseSchema = z.object({ pin: channelPinSchema });
